import { useState, useCallback } from 'react';

/**
 * useFormValidation — validates form fields and shows inline errors.
 *
 * @param {Object} rules - { fieldName: (value) => 'error message' | null }
 *
 * Usage:
 *   const { errors, validate, clearError, isValid } = useFormValidation({
 *     name: (v) => !v ? 'Name is required' : null,
 *     email: (v) => !v.includes('@') ? 'Valid email required' : null,
 *   });
 *
 *   // In submit handler:
 *   if (!validate(formValues)) return;
 *
 *   // In JSX:
 *   <FieldError error={errors.name} />
 */
export function useFormValidation(rules = {}) {
  const [errors, setErrors] = useState({});

  const validate = useCallback((values) => {
    const newErrors = {};
    let valid = true;
    for (const [field, ruleFn] of Object.entries(rules)) {
      const err = ruleFn(values[field]);
      if (err) { newErrors[field] = err; valid = false; }
    }
    setErrors(newErrors);
    return valid;
  }, [rules]);

  const clearError = useCallback((field) => {
    setErrors(e => { const n = { ...e }; delete n[field]; return n; });
  }, []);

  const isValid = Object.keys(errors).length === 0;

  return { errors, validate, clearError, isValid };
}

/**
 * FieldError — renders an inline validation error message.
 */
export function FieldError({ error }) {
  if (!error) return null;
  return <p className="text-xs text-danger mt-1 flex items-center gap-1">⚠ {error}</p>;
}
